import React from "react";

import { Outlet, useNavigate } from "react-router";
import { useLocation, useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import styled from "@mui/material/styles/styled";

import backgroundAvif from "@app/analytics/assets/report_bg_upsell.avif";
import backgroundWebp from "@app/analytics/assets/report_bg_upsell.webp";
import backgroundAvifMobile from "@app/analytics/assets/report_bg_upsell_mobile.avif";
import backgroundWebpMobile from "@app/analytics/assets/report_bg_upsell_mobile.webp";
import Tabs from "@app/analytics/components/Tabs";
import ROUTES from "@app/common/routes";
import { LocationWithState } from "@app/common/types";

import ReadLogo from "@rui/ReadLogo";
import Stack, { Column, Row } from "@rui/Stack";

import { TAB_STYLE_OVERRIDES } from "./styles";

const AUTH_TABS = [
  {
    id: "signup",
    label: "Create account",
    path: ROUTES.SIGN_UP,
    enabled: true,
  },
  {
    id: "signin",
    label: "Sign in",
    path: ROUTES.SIGN_IN,
    enabled: true,
  },
];

const BlurredBackground = styled(Box)(({ theme }) => ({
  width: "100%",
  minHeight: "100vh",
  backgroundSize: "100%",
  [theme.breakpoints.up("lg")]: {
    backgroundSize: "cover",
  },
  backgroundImage: `image-set(url(${backgroundAvifMobile}) type("image/avif"), url(${backgroundWebpMobile}) type("image/webp"))`,
  [theme.breakpoints.up("mobile")]: {
    backgroundImage: `image-set(url(${backgroundAvif}) type("image/avif"), url(${backgroundWebp}) type("image/webp"))`,
  },
  backgroundRepeat: "no-repeat",
  filter: "blur(5px)",
  position: "fixed",
  zIndex: 1,
}));

const GradientBackground = styled(Box)({
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundImage:
    "linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.3) 70%, #CCCCCCFF 100%)",
  width: "100%",
  height: "100vh",
  position: "fixed",
  zIndex: 2,
});

const CompositeBackground = () => {
  return (
    <>
      <BlurredBackground />
      <GradientBackground />
    </>
  );
};

const AuthLandingWithReport = () => {
  const [queryParams] = useSearchParams();
  const location = useLocation() as LocationWithState;
  const navigate = useNavigate();
  const zoomRedirect = queryParams.get("zoomRedirect");
  const redirectTo = queryParams.get("redirectTo");

  const handleAuthTabChange = (path: string) => {
    const stateRedirectTo =
      Boolean(zoomRedirect) || Boolean(redirectTo)
        ? { redirectTo: { pathname: zoomRedirect || redirectTo } }
        : location.state?.redirectTo;

    navigate(
      { pathname: path, search: location.search },
      {
        state: {
          ...location.state,
          redirectTo: stateRedirectTo,
        },
      }
    );
  };

  return (
    <>
      <CompositeBackground />

      <Column
        variant="center"
        sx={{ width: "100%", minHeight: "100vh", padding: "12px" }}
      >
        <Column variant="apart" sx={{ zIndex: 5 }}>
          <Paper
            elevation={3}
            sx={{
              borderRadius: "8px",
              padding: { xxs: "24px", mobile: "32px" },
              maxWidth: "454px",
            }}
          >
            <Column spacing={3}>
              <Stack width="100%" maxWidth="390px">
                <ReadLogo sx={{ mb: "28px" }} />
                <Row>
                  <Tabs
                    sx={TAB_STYLE_OVERRIDES}
                    tabs={AUTH_TABS}
                    onChange={handleAuthTabChange}
                    variant="fullWidth"
                  />
                </Row>
              </Stack>
              <Outlet />
            </Column>
          </Paper>
        </Column>
      </Column>
    </>
  );
};

export default AuthLandingWithReport;
